import { defaultState } from '@/components/listings/search/ListingSearchProvider/defaultState'
import formatFilters from '@/components/listings/search/ListingSearchProvider/formatFilters'
import type { ListingFiltersState } from '@/components/listings/search/ListingSearchProvider/types'

/**
 * Single-pass computation of all similar-inventory filter data.
 *
 * Returns:
 * - remainingFilters: full ListingFiltersState with relaxed keys reset to defaults
 *   (used to populate the widened search when the buyer clicks "Show more")
 * - appliedFilterKeys: FE-state keys with active values in the buyer's current search
 *   (sent to Segment as active_filters)
 * - removedFilterKeys: FE-state keys that were relaxed to generate similar results
 *   (sent to Segment as removed_filters)
 *
 * Uses formatFilters as the single source of truth for the state→BE-params mapping.
 */
export const computeSimilarInventoryFilters = (
  removedFilters: string[],
  currentFilters: ListingFiltersState
): {
  remainingFilters: ListingFiltersState
  appliedFilterKeys: string[]
  removedFilterKeys: string[]
} => {
  const removed = new Set(removedFilters)
  const appliedFilterKeys: string[] = []
  const removedFilterKeys: string[] = []
  const clearedEntries: Partial<ListingFiltersState> = {}

  for (const key of Object.keys(
    currentFilters
  ) as (keyof ListingFiltersState)[]) {
    const singleKeyState: ListingFiltersState = {
      ...defaultState.filters,
      [key]: currentFilters[key],
    }
    const beKeys = Object.keys(formatFilters(singleKeyState))

    if (beKeys.length > 0) {
      appliedFilterKeys.push(key)

      if (removed.size > 0 && beKeys.some((beKey) => removed.has(beKey))) {
        removedFilterKeys.push(key)
        Object.assign(clearedEntries, { [key]: defaultState.filters[key] })
      }
    }
  }

  return {
    // Return same reference when nothing was removed — preserves referential stability
    remainingFilters:
      removedFilterKeys.length === 0
        ? currentFilters
        : { ...currentFilters, ...clearedEntries },
    appliedFilterKeys,
    removedFilterKeys,
  }
}
